import { Injectable, Injector, NgZone, PLATFORM_ID, effect, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getRemoteConfig, fetchAndActivate, setCustomSignals, getBoolean, RemoteConfig } from 'firebase/remote-config';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

/**
 * Firebase Remote Config for console — THROWAWAY FRONTEND UI EXPERIMENTS ONLY.
 *
 * This is deliberately NOT the mechanism for backend-connector GA gates
 * (those stay as `environment.*.ts` booleans like `snowflakeEnabled` /
 * `dynamodbEnabled`, or migrate to the audited per-tenant entitlement system
 * if they ever need to vary per tenant) — a Remote Config flag only changes
 * what the browser renders; core-api has no idea it exists and enforces
 * nothing from it. Use this for short-lived, low-stakes presentation-layer
 * experiments (a copy test, a layout variant, a staged nav change) that
 * don't need an audit trail.
 *
 * Mirrors `website`'s FirebaseConfigService/FeatureFlagsService pattern
 * (browser-only init, fetch fired outside the Angular zone so it never
 * blocks change-detection stability), adapted for console's real identity
 * signals instead of analytics consent.
 *
 * Targeting note: Remote Config's built-in conditions (app version, platform,
 * country, percentage) know nothing about DataRecs tenants or users — you set
 * *custom signals* yourself (below) and write conditions against those values
 * in the Firebase console UI. Signals are set from `AuthService.user()`, so
 * flags can only be tenant/user-targeted AFTER login — there is no
 * before-login user context to target on, by design (login-page UI isn't in
 * scope for this service).
 */
@Injectable({ providedIn: 'root' })
export class RemoteFlagsService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly ngZone = inject(NgZone);
  private readonly auth = inject(AuthService);
  private readonly injector = inject(Injector);

  private app: FirebaseApp | null = null;
  private remoteConfig: RemoteConfig | null = null;
  private bootstrapped = false;
  // Bumped on every activateFor() call so an in-flight setCustomSignals/fetchAndActivate
  // chain from a stale user (e.g. logout->different-user login mid-request) can detect
  // it's no longer current and skip applying its result — the last-fired call always wins.
  private activationGeneration = 0;

  /**
   * Not called by app.config.ts's APP_INITIALIZER directly — `AuthService.loadUser()`
   * fire-and-forgets its HTTP call rather than returning something the initializer
   * chain can await, so there is no single moment "after login resolves" to hang a
   * second initializer off. Instead, `bootstrapWhenReady()` is called from the same
   * initializer alongside `authService.loadUser()` and reacts to the `auth.user`
   * signal via `effect()`: it does nothing while the signal is unset (still loading,
   * OR the user is genuinely logged out — those are indistinguishable from a signal
   * read alone, which is fine, since logged-out users intentionally get no Remote
   * Config context here), and fires exactly once the first time `user()` becomes
   * non-null. A logout → different-user login within the same tab re-fires it (the
   * effect re-runs whenever `user()` changes), keeping signals in sync with whoever
   * is actually signed in.
   */
  bootstrapWhenReady(): void {
    if (this.bootstrapped) return; // idempotent — only one effect() should ever be armed
    if (!isPlatformBrowser(this.platformId)) return;
    if (environment.demoMode) return; // never make a real network call in demo mode
    const config = (environment as { firebase?: Record<string, string> }).firebase;
    if (!config) return; // not configured for this environment (local dev)

    this.bootstrapped = true;
    effect(() => {
      const user = this.auth.user();
      if (!user) return; // still loading, or genuinely logged out — no-op either way
      this.activateFor(user.id, user.email, user.roles ?? [], user.last_login_at);
    }, { injector: this.injector });
  }

  private activateFor(userId: string, userEmail: string, userRoles: string[], lastLoginAt: Date | undefined): void {
    if (!this.app) {
      const config = (environment as { firebase?: Record<string, string> }).firebase!;
      this.app = initializeApp(config);
      this.remoteConfig = getRemoteConfig(this.app);
      this.remoteConfig.settings.minimumFetchIntervalMillis =
        environment.production ? 3_600_000 : 0;
      // In-code defaults — used if Remote Config has never fetched or a key is missing.
      this.remoteConfig.defaultConfig = {
        // Proof-of-plumbing flag for the first slice (see docs). Safe/off default.
        demo_banner_enabled: false,
      };
    }
    const rc = this.remoteConfig!;

    const daysSinceLastLogin = lastLoginAt
      ? Math.floor((Date.now() - new Date(lastLoginAt).getTime()) / 86_400_000)
      : -1; // unknown — first session or field absent

    const generation = ++this.activationGeneration;

    // Fire-and-forget outside the Angular zone: these async tasks must not gate
    // change-detection stability (same rationale as website's NG0506 fix).
    this.ngZone.runOutsideAngular(() => {
      void setCustomSignals(rc, {
        user_id: userId,
        user_email: userEmail,
        user_roles: userRoles.join(','),
        days_since_last_login: daysSinceLastLogin,
      })
        .then(() => {
          if (generation !== this.activationGeneration) return; // superseded by a newer user
          return fetchAndActivate(rc);
        })
        .catch(() => { /* offline / blocked — keep defaultConfig values */ });
    });
  }

  /** Exposes the Remote Config instance for advanced use in other services/components. */
  getRemoteConfig(): RemoteConfig | null {
    return this.remoteConfig;
  }

  /**
   * Proof-of-plumbing flag for the first Remote Config slice — see the
   * Firebase Remote Config design doc. Throwaway: delete this getter (and
   * the `demo_banner_enabled` parameter in the Firebase console) once a real
   * experiment replaces it.
   */
  get demoBannerEnabled(): boolean {
    if (!isPlatformBrowser(this.platformId) || !this.remoteConfig) return false;
    return getBoolean(this.remoteConfig, 'demo_banner_enabled');
  }
}
